import { useState, useRef } from "react";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle,
  Loader2, Database, ArrowRight,
} from "lucide-react";

export default function AdminMidImport() {
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [report, setReport] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pick = (f) => { setFile(f); setReport(null); setResult(null); };

  const validate = async () => {
    if (!file) return;
    setValidating(true); setReport(null); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/mid/validate", fd);
      setReport(r.data);
      toast[r.data.valid ? "success" : "error"](r.data.valid ? "Validation passed" : "Validation found blocking errors");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    } finally { setValidating(false); }
  };

  const runImport = async () => {
    if (!file || !report?.valid) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("mode", "replace");
      const r = await api.post("/admin/mid/import", fd);
      setResult(r.data.import);
      toast.success(`Imported successfully (v${r.data.import.version})`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Import failed");
    } finally { setImporting(false); }
  };

  const s = report?.summary;

  return (
    <div data-testid="admin-mid-import-page" className="space-y-6 max-w-5xl">
      <SEOHead title="MID Import" />
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Database className="h-6 w-6 text-[#0B2A6B]" /> MID Import Engine
        </h1>
        <p className="text-sm text-slate-500">Upload the Master Intelligent Data workbook. Validate first — import is allowed only after validation passes.</p>
      </div>

      {/* Upload */}
      <div
        data-testid="mid-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
        className="cursor-pointer rounded-2xl border-2 border-dashed border-slate-300 bg-white p-8 text-center transition-colors hover:border-[#0B2A6B]"
      >
        <input ref={inputRef} type="file" accept=".xlsx" hidden data-testid="mid-file-input"
          onChange={(e) => pick(e.target.files?.[0])} />
        <UploadCloud className="mx-auto h-10 w-10 text-[#0B2A6B]" />
        {file ? (
          <p className="mt-3 inline-flex items-center gap-2 font-medium text-slate-800"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> {file.name}</p>
        ) : (
          <p className="mt-3 text-slate-500">Click or drag a <span className="font-semibold">.xlsx</span> workbook here</p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <button data-testid="mid-validate-btn" onClick={validate} disabled={!file || validating}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40 hover:bg-slate-700">
          {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Validate
        </button>
        <button data-testid="mid-import-btn" onClick={runImport} disabled={!report?.valid || importing}
          className="inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40 hover:bg-[#081E4D]">
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Import to Database
        </button>
      </div>

      {/* Result banner */}
      {result && (
        <div data-testid="mid-import-result" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <p className="flex items-center gap-2 font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Import v{result.version} completed in {(result.durationMs/1000).toFixed(1)}s</p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {Object.entries(result.counts).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-white p-3"><p className="font-display text-xl font-bold text-slate-900">{v}</p><p className="text-xs capitalize text-slate-500">{k}</p></div>
            ))}
          </div>
        </div>
      )}

      {/* Validation report */}
      {report && (
        <div className="space-y-4" data-testid="mid-validation-report">
          <div className={`rounded-xl border p-4 ${report.valid ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
            {report.valid
              ? <p className="flex items-center gap-2 font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Validation passed — ready to import</p>
              : <p className="flex items-center gap-2 font-semibold text-red-700"><XCircle className="h-5 w-5" /> {report.errors.length} blocking error(s) — fix the workbook before importing</p>}
          </div>

          {s && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[["States", s.states], ["Cities", s.cities], ["Colleges", s.colleges], ["Courses", s.courses],
                ["Fees", s.fees], ["Placements", s.placements], ["Images", s.images], ["Sources", s.sources]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-slate-200 bg-white p-4"><p className="font-display text-2xl font-extrabold text-slate-900">{v}</p><p className="text-xs text-slate-500">{k}</p></div>
              ))}
            </div>
          )}

          {s?.byState?.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="mb-2 text-sm font-bold text-slate-900">Colleges by State</h3>
              <div className="flex flex-wrap gap-2">
                {s.byState.map((b) => <span key={b.state} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{b.state}: <b>{b.colleges}</b></span>)}
              </div>
            </div>
          )}

          {report.errors.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-white p-4" data-testid="mid-errors">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-red-700"><XCircle className="h-4 w-4" /> Errors ({report.errors.length})</h3>
              {report.errorDetails?.length > 0 ? (
                <div className="max-h-80 overflow-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="text-slate-400"><tr>
                      <th className="py-1 pr-3">Sheet</th><th className="py-1 pr-3">Row</th><th className="py-1 pr-3">ID</th>
                      <th className="py-1 pr-3">Column</th><th className="py-1 pr-3">Error</th><th className="py-1">Recommended Fix</th>
                    </tr></thead>
                    <tbody>
                      {report.errorDetails.map((e, i) => (
                        <tr key={i} className="border-t border-slate-100 align-top">
                          <td className="py-1 pr-3 font-medium text-slate-700">{e.sheet}</td>
                          <td className="py-1 pr-3 text-slate-600">{e.row}</td>
                          <td className="py-1 pr-3 font-mono text-slate-700">{e.collegeId}</td>
                          <td className="py-1 pr-3 text-slate-600">{e.column}</td>
                          <td className="py-1 pr-3 text-red-600">{e.error}</td>
                          <td className="py-1 text-slate-600">{e.fix}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <ul className="space-y-1 text-sm text-slate-700 max-h-64 overflow-auto">
                  {report.errors.map((e, i) => <li key={i} className="flex gap-2"><ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" />{e}</li>)}
                </ul>
              )}
            </div>
          )}
          {report.warnings.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-white p-4" data-testid="mid-warnings">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-700"><AlertTriangle className="h-4 w-4" /> Warnings ({report.warnings.length})</h3>
              <ul className="space-y-1 text-sm text-slate-600 max-h-48 overflow-auto">
                {report.warnings.map((w, i) => <li key={i} className="flex gap-2"><ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />{w}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
